import { useAuthContext } from '../../context/auth/auth'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import instance from '../api'
import { CURRENT_SUBSCRIPTION_QUERY_KEY } from './useCurrentSubscription'
import { toast } from 'react-toastify'
import { useTranslation } from 'react-i18next'
import { getApiErrorMessage } from '../apiError'

interface CancelSubscriptionResponse {
  action: string
  cancel_at_period_end: boolean
  message: string
}

/** Why the customer is leaving, collected by `ModalCancelSubscription`. Both
 *  fields are optional to the backend, which reads a missing one as "not
 *  given" and emails the super admins accordingly. */
export interface CancelSubscriptionFeedback {
  reason?: string
  comment?: string
}

/**
 * Only non-empty values are sent. The endpoint accepts `""` and `null` as
 * "not given" too, so this is about not claiming the customer answered when
 * they did not - and the restore direction, which validates the body and then
 * ignores it, posts nothing at all rather than a reason for an uncancelling.
 */
const cancelSubscriptionBody = ({
  reason,
  comment,
}: CancelSubscriptionFeedback) => {
  const body: CancelSubscriptionFeedback = {}
  const trimmedReason = reason?.trim()
  const trimmedComment = comment?.trim()

  if (trimmedReason) body.reason = trimmedReason
  if (trimmedComment) body.comment = trimmedComment

  return body
}

const cancelSubscription = async (
  subscriptionId: number,
  token: string,
  feedback: CancelSubscriptionFeedback,
): Promise<CancelSubscriptionResponse> => {
  const response = await instance.post(
    `/payment/subscriptions/${subscriptionId}/cancel/`,
    cancelSubscriptionBody(feedback),
    {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    },
  )

  return response.data.data
}

const useCancelSubscription = () => {
  const { token } = useAuthContext()
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({
      subscriptionId,
      ...feedback
    }: { subscriptionId: number } & CancelSubscriptionFeedback) =>
      cancelSubscription(subscriptionId, token!, feedback),
    onSuccess: async (data) => {
      await queryClient.invalidateQueries({
        queryKey: CURRENT_SUBSCRIPTION_QUERY_KEY,
      })
      toast.success(
        t(
          data.cancel_at_period_end
            ? 'subscriptionCancelled'
            : 'subscriptionReactivated',
        ),
      )
    },
    onError: (error) => {
      // The backend refuses a bank transfer cancellation with an explanation
      // worth reading ("managed by the ReportAll team - contact support").
      // Always fall back to a generic message: `toast.error(undefined)`
      // renders no toast at all, so a missed shape used to fail in silence.
      // A rejected reason or an overlong comment arrives here too, as a DRF
      // field error, which `getApiErrorMessage` already reads.
      toast.error(
        getApiErrorMessage(error) ?? t('somethingWentWrongPleaseTryAgain'),
      )
    },
  })
}

export default useCancelSubscription
